import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../../api/person/queries";
import { personForeningForm } from "../../../../../config/form/person/personForeningFormConfig";
import { defaultForeningPersonColumns } from "../../../../../config/person";
import { useGenericQuery } from "../../../../../hooks/useQueries";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import ActiveIcon from "../../../../icons/ActiveIcon";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import { PersonDto } from "../../../../../interfaces/schema";
import { PersonForeningField } from "../../../../../interfaces/personer";
import { SortBy, IColumn } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { periodeIsActive } from "../../../../../util/date";
import { orderBy } from "../../../../../util/orderBy";

export default function Medlemskaber({
  foreningPersonColumns,
  invalidateData,
  person,
  setForeningPersonActiveColumns,
}: {
  invalidateData: InvalidateDataFunction<any>;
  person: PersonDto;
  foreningPersonColumns: IColumn<PersonForeningField>[];
  setForeningPersonActiveColumns: React.Dispatch<
    React.SetStateAction<PersonForeningField[]>
  >;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "BasisWrite");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personMedlemskaberSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );
  const queryValues = useGenericQuery<IForening>("foreninger", {
    orderBy: "navn",
  });

  const { data } = useQuery<{
    person: PersonDto;
  }>(PERSON_BY_ID_QUERY(), {
    variables: { id: person.id },
  });

  // event handlers
  function downloadHandler() {
    tableToXLSX(foreningPersonColumns, person?.foreningPerson ?? [], {
      fileName: `medlemsskaber-for-${data?.person.kundeNr}`,
      additionalData: {
        Kundenr: data?.person.kundeNr,
      },
    });
  }

  if (person.foreningPerson?.length === 0) {
    return (
      <IngenRegistreringer
        editConfig={
          canWrite ? personForeningForm(person.id, queryValues) : undefined
        }
        invalidateData={invalidateData}
      />
    );
  }

  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <ModalProvider>
        <TableRoot>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            <>
              <th className="indicator"></th>
              {getHeaderActiveColumns(foreningPersonColumns, {
                defaultSortBy,
                setSortBy,
                sortBy,
              })}
            </>
          </TableHead>
          <TableBody>
            {orderBy(
              person.foreningPerson,
              sortBy.map((s) => s.field),
              sortBy.map((s) => s.direction),
            ).map((fp) => {
              return (
                <TableRow
                  key={fp.id}
                  editConfig={
                    canWrite
                      ? personForeningForm(person.id, queryValues, fp)
                      : undefined
                  }
                  invalidateData={invalidateData}
                >
                  <td
                    className={`indicator${
                      periodeIsActive(
                        new Date(fp.periode.fraDato),
                        new Date(fp.periode.tilDatoInklusiv),
                      )
                        ? " has-effect"
                        : ""
                    }`}
                  >
                    <ActiveIcon />
                  </td>
                  <>
                    {foreningPersonColumns
                      .filter((col) => col.active)
                      .map((col) => {
                        return <TD key={col.field} col={col} rowData={fp} />;
                      })}
                  </>
                </TableRow>
              );
            })}
          </TableBody>
        </TableRoot>
        <ColumnEditorModal
          defaultColumns={defaultForeningPersonColumns}
          columns={foreningPersonColumns}
          setActiveColumns={setForeningPersonActiveColumns}
        />
        <OpretNyButtonBlock
          editConfig={
            canWrite ? personForeningForm(person.id, queryValues) : undefined
          }
          invalidateData={invalidateData}
        />
      </ModalProvider>
    </>
  );
}

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];
